/**
 * Body-portaled tooltips for composer reference chips.
 * Native title + mouseenter-only dismiss leaves a stuck label after paste:
 * the chip appears under the cursor, mouseenter fires, then React replaces
 * the node so mouseleave never runs.
 */

export const REFERENCE_CHIP_TOOLTIP_ATTR = 'data-flownotes-ref-chip-tooltip'

/** Ignore the synthetic hover when a chip is inserted under the pointer (paste). */
export const CHIP_TOOLTIP_HOVER_GRACE_MS = 400

const tooltipByChip = new WeakMap<HTMLElement, HTMLElement>()
const cleanupByChip = new WeakMap<HTMLElement, () => void>()

export function buildReferenceChipTooltipText(reference: {
  path?: string
  title?: string
  lineStart?: number
  lineEnd?: number
}): string {
  const parts: string[] = []
  if (reference.path) {
    parts.push(reference.path)
  } else if (reference.title) {
    parts.push(reference.title)
  }
  if (reference.lineStart !== undefined) {
    if (reference.lineEnd !== undefined && reference.lineEnd !== reference.lineStart) {
      parts.push(`Lines ${reference.lineStart}-${reference.lineEnd}`)
    } else {
      parts.push(`Line ${reference.lineStart}`)
    }
  }
  return parts.join(' • ')
}

export function dismissReferenceChipTooltip(chip: HTMLElement) {
  cleanupByChip.get(chip)?.()
  cleanupByChip.delete(chip)
  const tip = tooltipByChip.get(chip)
  if (tip?.isConnected) tip.remove()
  tooltipByChip.delete(chip)
}

/** After full editor clear; chips may be gone while tooltips still sit on document.body. */
export function removeAllPortaledReferenceChipTooltips() {
  document.querySelectorAll(`[${REFERENCE_CHIP_TOOLTIP_ATTR}]`).forEach((el) => el.remove())
}

function pointerIsOverChip(chip: HTMLElement, target: EventTarget | null): boolean {
  return Boolean(
    chip.isConnected && target instanceof Node && chip.contains(target),
  )
}

export function showReferenceChipTooltip(chip: HTMLElement, text: string) {
  dismissReferenceChipTooltip(chip)
  if (!text || typeof document === 'undefined') return

  const rect = chip.getBoundingClientRect()
  const tooltipElement = document.createElement('div')
  tooltipElement.setAttribute(REFERENCE_CHIP_TOOLTIP_ATTR, '')
  tooltipElement.className = 'fixed z-[10000] pointer-events-none'
  tooltipElement.style.top = `${rect.top - 8}px`
  tooltipElement.style.left = `${rect.left + rect.width / 2}px`
  tooltipElement.style.transform = 'translate(-50%, -100%)'

  const tooltipContent = document.createElement('div')
  tooltipContent.className =
    'bg-popover border border-border rounded-md px-2.5 py-1.5 text-xs text-popover-foreground shadow-lg whitespace-nowrap max-w-md'
  tooltipContent.textContent = text

  const arrow = document.createElement('div')
  arrow.className =
    'absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-popover'

  tooltipElement.appendChild(tooltipContent)
  tooltipContent.appendChild(arrow)
  document.body.appendChild(tooltipElement)
  tooltipByChip.set(chip, tooltipElement)

  const onPointer = (event: Event) => {
    if (pointerIsOverChip(chip, event.target)) return
    dismissReferenceChipTooltip(chip)
  }
  document.addEventListener('pointermove', onPointer, true)
  document.addEventListener('pointerdown', onPointer, true)
  cleanupByChip.set(chip, () => {
    document.removeEventListener('pointermove', onPointer, true)
    document.removeEventListener('pointerdown', onPointer, true)
  })
}

export function armReferenceChipTooltipGrace(chip: HTMLElement, now = Date.now()) {
  chip.dataset.chipTooltipArmedAt = String(now + CHIP_TOOLTIP_HOVER_GRACE_MS)
}

export function shouldIgnoreChipTooltipHover(chip: HTMLElement, now = Date.now()): boolean {
  const armedAt = Number(chip.dataset.chipTooltipArmedAt || 0)
  return armedAt > now
}
